import { useContext, useState, useRef } from "react"
import { FilterMatchMode } from "primereact/api"
import { useQuery, useMutation } from "@apollo/client"
import _ from "lodash"
import { Link, useNavigate } from "react-router-dom"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Dropdown } from "primereact/dropdown"
import { InputText } from 'primereact/inputtext'
import { SplitButton } from "primereact/splitbutton"
import { Messages } from "primereact/messages"
import { LoaderMedium } from "../../components/Loaders"
import EnterpriseLibrarySidebar from "../../components/sidebars/EnterpriseLibrarySidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import { APP_MANAGER_ROLE, ENTERPRISE_ADMIN_ROLE, SYSTEM_ADMIN_ROLE } from "../../helpers/staffRoleHelpers"

// Context
import { UserContext } from "../../context/userContext"

// Queries
import { GET_FIELDS_LIBRARY, DELETE_CUSTOM_FIELD } from "./queries"

// Styles
import styles from "../../styles/Fields-Library.module.css"

export default function Fields() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const msgs1 = useRef(null)
  const [libraryListSystem, setLibraryListSystem] = useState(null)
  const [fieldTypeListSystem, setFieldTypeListSystem] = useState(null)
  const [libraryListCustom, setLibraryListCustom] = useState(null)
  const [fieldTypeListCustom, setFieldTypeListCustom] = useState(null)
  const [inUseFields, setInUseFields] = useState([])
  const [filters] = useState({
    field_library: { value: null, matchMode: FilterMatchMode.EQUALS },
    field_name_report: { value: null, matchMode: FilterMatchMode.CONTAINS },
    field_label: { value: null, matchMode: FilterMatchMode.CONTAINS },
    field_type: { value: null, matchMode: FilterMatchMode.EQUALS },
  })
  const [deleteField] = useMutation(DELETE_CUSTOM_FIELD)

  // Roles that can access 'sys' fields
  const typeSysAccessRoles = [
    SYSTEM_ADMIN_ROLE,
    APP_MANAGER_ROLE,
    ENTERPRISE_ADMIN_ROLE,
  ]

  // Roles that can access 'hidden' fields
  const typeHiddenAccessRoles = [
    SYSTEM_ADMIN_ROLE,
    APP_MANAGER_ROLE,
  ]

  const { loading, data, refetch } = useQuery(GET_FIELDS_LIBRARY, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
    },
    skip: !userCtx,
    onCompleted: (data) => {
      // Set Library List System
      const allLibraries = _.map(
        data.systemFields,
        ({ field_library }) => field_library
      )
      setLibraryListSystem(_.uniq(allLibraries).sort())
      setInUseFields(_.map(data.customFields, ({ field_name }) => field_name))

      // Set Field Type List System
      const allTypes = _.map(
        data.systemFields,
        ({ field_type }) => field_type
      )
      setFieldTypeListSystem(_.uniq(allTypes).sort())

      // Set Library List Custom
      const allLibrariesCustom = _.map(
        data.customFields,
        ({ field_library }) => field_library
      )
      setLibraryListCustom(_.uniq(allLibrariesCustom).sort())

      // Set Field Type List Custom
      const allTypesCustom = _.map(
        data.customFields,
        ({ field_type }) => field_type
      )
      setFieldTypeListCustom(_.uniq(allTypesCustom).sort())
    },
  })

  if (loading) return <LoaderMedium />

  const handleSysFields = (fields) => {
    const listFields = _.reduce(
      fields,
      (acc, field) => {
        const skipField = () => {
          // Map field types to roles that can access them
          const userRole = Number(userCtx.session.user.staff_role)
          const rolePermissions = {
            sys: typeSysAccessRoles,
            hidden: typeHiddenAccessRoles,
          }

          // Check if the field type has role restrictions
          const allowedRoles = rolePermissions[field.field_type]
          if (allowedRoles) {
            return !allowedRoles.includes(userRole)
          }

          return false
        }

        if (skipField()) return acc

        const handleCustomize = () => {
          const fieldLock = !field.allow_custom_config
          const isInUseField = _.includes(inUseFields, field.field_name)
          if (fieldLock) {
            return (
              <div className={styles.actionBtn}>
                <span className="material-icons">lock</span>Locked
              </div>
            )
          }
          if (isInUseField) {
            return (
              <div className={styles.inUse}>
                <span className="material-icons">check_box</span>
                Custom Active
              </div>
            )
          }
          return (
            <div className={styles.actionBtn}>
              <Link to={`/library/customize/${field.idField}`}>
                <span className="material-icons">edit</span>Customize
              </Link>
            </div>
          )
        }

        return [
          ...acc,
          {
            ...field,
            actions: handleCustomize(),
          },
        ]
      },

      []
    )

    return _.orderBy(listFields, ["field_library"])
  }

  const handleCustomFields = (fields) => {
    return _.map(fields, (field) => {
      const handleActionBtn = () => {
        const items = [
          {
            label: "Delete",
            icon: "pi pi-trash",
            command: async () => {
              await deleteField({
                variables: {
                  id: field.idField,
                },
                onError: (err) => {
                  msgs1.current.show([
                    {
                      severity: "error",
                      detail: err.message,
                      sticky: true,
                    },
                  ])
                },
              })
              refetch()
            },
          },
        ]
        return (
          <SplitButton
            label="Edit"
            model={items}
            className="mb-2 mr-2 p-button-sm"
            onClick={() => navigate(`/library/field/edit/${field.idField}`)}
          />
        )
      }
      return {
        ...field,
        actions: handleActionBtn(),
      }
    })
  }

  const libraryRowFilterTemplate = (options, type) => {
    return (
      <Dropdown
        value={options.value}
        options={type === "system" ? libraryListSystem : libraryListCustom}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select a Library"
        className="p-column-filter"
      />
    )
  }

  const fieldNameRowFilterTemplate = (options) => {
    return (
      <InputText
        value={options.value}
        onChange={(e) => options.filterApplyCallback(e.target.value)}
        placeholder="Search by Field Name"
        className="p-column-filter"
      />
    )
  }

  const fieldLabelRowFilterTemplate = (options) => {
    return (
      <InputText
        value={options.value}
        onChange={(e) => options.filterApplyCallback(e.target.value)}
        placeholder="Search by Field Label"
        className="p-column-filter"
      />
    )
  }

  const fieldTypeRowFilterTemplate = (options, type) => {
    return (
      <Dropdown
        value={options.value}
        options={type === "system" ? fieldTypeListSystem : fieldTypeListCustom}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select a Field Type"
        className="p-column-filter"
      />
    )
  }

  const FieldDataTable = ({ type }) => {
    return (
      <DataTable
        key={type}
        value={type === "system" ? handleSysFields(data.systemFields) : handleCustomFields(data.customFields)}
        className={styles.table}
        filters={filters}
        responsiveLayout="scroll"
        sortField="field_library"
        sortOrder={1}
      >
        <Column
          field="field_library"
          sortable
          filter
          header="Library"
          showFilterMatchModes={false}
          filterElement={(options) => libraryRowFilterTemplate(options, type)}
        />
        <Column
          field="description"
          sortable header="Data Dictionary"
        />
        <Column
          field="field_name_report"
          sortable
          header="Field Name"
          filter
          showFilterMatchModes={false}
          filterElement={fieldNameRowFilterTemplate}
        />
        <Column
          field="field_label"
          sortable
          header="Field Label"
          filter
          showFilterMatchModes={false}
          filterElement={fieldLabelRowFilterTemplate}
        />
        <Column
          field="field_type"
          sortable
          header="Field Type"
          filter
          showFilterMatchModes={false}
          filterElement={(options) => fieldTypeRowFilterTemplate(options, type)}
        />
        <Column
          field="actions"
          header="Actions"
          style={{ width: 180 }}
        />
      </DataTable>
    )
  }

  return (
    <div className="page-fields">
      <PageSidebarContent>
        <EnterpriseLibrarySidebar />
      </PageSidebarContent>
      <div className="main-content">
        <h1 className={styles.title}>
          Library <span>Fields</span>
        </h1>
        <div className={styles.description}>
          Administrators can review, customize, and add new field variables to
          their RecoveryLink application to be used by member Organizations.
          Fields need to be created or updated to be available for application
          blocks and templates.
        </div>

        <h1 className={styles.subTitle}>Default Fields</h1>

        <div className={styles.totalFields}>
          Total Fields: {data.systemFields.length}
        </div>

        <FieldDataTable type="system" />

        <h1 className={styles.subTitle}>Custom Fields</h1>
        <Link to="/library/field/add" className="btn">
          <div className="flex flex-row gap-2">
            <span className="material-icons">add</span>
            <span>Add New Field</span>
          </div>
        </Link>

        <div style={{ margin: "10px 0" }}>
          <Messages ref={msgs1} />
        </div>

        <FieldDataTable type="custom" />

      </div>
    </div>
  )
}
